import * as ToggleSwitch from '@radix-ui/react-switch';
import React, { useCallback, KeyboardEvent, useState, useMemo } from 'react';
import styles from './Switch.module.scss';
import { TestIds } from './testIds';

export interface SwitchProps {
  'offLabel'?: string;
  'onLabel'?: string;
  'value': boolean;
  'onValueChange': (checked: boolean) => void;
  'disabled'?: boolean;
  'isError'?: boolean;
  'isMandatory'?: boolean;
  'data-testid'?: string;
}

const Switch = ({
  offLabel,
  onLabel,
  value,
  onValueChange,
  disabled = false,
  isError,
  isMandatory = false,
  'data-testid': dataTestId = TestIds.switch.root,
}: SwitchProps) => {
  const [isDirty, setIsDirty] = useState<boolean>(false);

  const checked = !!value;

  const isNotDirtyAnyMore = useMemo(() => {
    if (!isMandatory) return true;
    return isDirty && isMandatory;
  }, [isDirty, isMandatory]);

  const handleToggle = useCallback(() => {
    onValueChange(!value);
    setIsDirty(true);
  }, [onValueChange, value]);

  const onPressEnter = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'enter') handleToggle();
    },
    [handleToggle],
  );

  return (
    <div
      className={styles.switch}
      data-testid={TestIds.switch.container}
      data-is-error={isError}
      data-is-disabled={disabled}
      data-is-mandatory={!isNotDirtyAnyMore}
    >
      {offLabel && (
        <div className={styles.label} data-is-active={!checked} data-testid={TestIds.switch.offLabel}>
          {offLabel}
        </div>
      )}
      <ToggleSwitch.Root
        className={styles.switchRoot}
        checked={checked}
        onCheckedChange={handleToggle}
        onKeyDown={onPressEnter}
        data-testid={dataTestId}
        disabled={disabled}
      >
        <ToggleSwitch.Thumb className={styles.switchThumb} data-testid={TestIds.switch.thumb} />
      </ToggleSwitch.Root>
      {onLabel && (
        <div className={styles.label} data-is-active={checked} data-testid={TestIds.switch.onLabel}>
          {onLabel}
        </div>
      )}
    </div>
  );
};

export default Switch;
